/* MyTomette · carte gravée — le studio guidé de la fiche produit (Lot F, 15/10/2026).
 *
 * Six étapes (cahier §5.2, D22) : Lieu → Format → Cadrage → Style et repère → Vos mots → Prête.
 *  - Ordinateur : aperçu collant à gauche, étapes en révélation progressive à droite (faites en noir
 *    avec leur résumé et « Modifier », active ouverte, à venir grisées).
 *  - Téléphone : guide plein écran, une décision par écran ; les corps des étapes du bureau y sont
 *    DÉPLACÉS (marqueurs <!--carte-guide-position-->), puis remis en place : jamais de formulaire en
 *    double. Squelette repris du guide de mtp-photo-studio.js (openGuide, guidePhase, guideViewport,
 *    guideTextCamera, updateFloating), réécrit pour la carte.
 *
 * Qui fait quoi :
 *  - window.CarteLieu (Lot R) : recherche (IGN et Photon à la frappe, Nominatim sur Entrée seulement)
 *    et « Précisez l'endroit » (épingle fixe, inverse IGN / Photon, jamais Nominatim) ;
 *  - window.CarteRendu (Lot R) : échelle conseillée, squelette de l'aperçu vivant, GRAVURE (le SVG
 *    même qui partira au laser), gabarit texte, formats, mise en page des textes (copie de mtLayoutText) ;
 *  - window.CartePeintre (ce lot) : l'aperçu sur la vraie photo de la pièce ;
 *  - mt-carte-produit.js (ce lot, chargé à l'étape Format) : le panier, par le moteur expert caché.
 *
 * L'état tient en moins de 1 Ko : il fait le brouillon (localStorage, 7 jours, sous try/catch) et le
 * lien de reprise #carte=<base64url> (un fragment : rien ne part au serveur). La carte se redessine
 * sur n'importe quel appareil à partir de cet état.
 */
(() => {
  'use strict';
  const CHANNEL = 'mt-carte-product';
  const PIECES = {hexa: {W: 120, H: 100}, carree: {W: 130, H: 130}};
  const FENETRE_DEFAUT = {hexa: 'pleine', carree: 'legende'};      // D7 : une mise en page par forme en V1
  const TROUS = {hexa: [[60, 15]], carree: [[15, 15], [115, 15]]};  // holeCenters du moteur (mm)
  const CORPS_MIN = 3.0;                                           // D10 : aucun texte du gabarit sous 3,0 mm de corps
  const TEINTE = '#6f5331';                                        // la teinte de la gravure dans l'aperçu (moteur, peintre)
  const STYLES = ['classique', 'taille-douce', 'epure'];
  const REPERES = ['epingle-coeur', 'epingle', 'coeur', 'aucun'];
  const DATES = ['points', 'maison', 'long', 'libre', 'sans'];
  const COORDS = ['dms', 'dec', 'sans'];
  const BROUILLON_JOURS = 7;

  /* base64url d'une chaîne UTF-8, et retour */
  function versB64(s) {
    const b = new TextEncoder().encode(s); let bin = '';
    for (let i = 0; i < b.length; i++) bin += String.fromCharCode(b[i]);
    return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
  function depuisB64(s) {
    const bin = atob(String(s).replace(/-/g, '+').replace(/_/g, '/'));
    return new TextDecoder('utf-8', {fatal: true}).decode(Uint8Array.from(bin, c => c.charCodeAt(0)));
  }
  const borne = (v, a, b) => Math.max(a, Math.min(b, v));
  const r1 = v => Math.round(v * 10) / 10, r2 = v => Math.round(v * 100) / 100, r5 = v => Math.round(v * 1e5) / 1e5;
  const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : 'mtc' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10));
  const reduit = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

  function mount(root) {
    if (root.dataset.carteStudio) return;
    root.dataset.carteStudio = 'true';
    const config = JSON.parse(root.querySelector('[data-carte-config]').textContent);
    const en = /^en\b/i.test(config.locale || '');
    const esp = !en && /^es\b/i.test(config.locale || '');
    const T_ES = esp ? {
      "tomette": "tomette",
      "carreau": "baldosa cuadrada",
      "Aperçu de votre ": "Vista previa de tu ",
      "carte de ": "mapa de ",
      " (exemple)": " (ejemplo)",
      " (en cours de dessin)": " (dibujándose)",
      "Exemple · Vieux-Port, Marseille": "Ejemplo · Vieux-Port, Marsella",
      "Votre carte se grave…": "Grabando tu mapa…",
      "Zone trop détaillée : rapprochez-vous.": "Zona demasiado detallada: acércate.",
      "La carte s’arrête à 85° de latitude : choisissez un lieu plus proche de l’équateur.": "El mapa se detiene a 85° de latitud: elige un lugar más cercano al ecuador.",
      "La carte ne peut pas être chargée pour le moment.": "El mapa no se puede cargar en este momento.",
      "Votre carte se dessine…": "Dibujando tu mapa…",
      "Pour trouver votre lieu, votre recherche est envoyée à des services cartographiques publics (IGN, Photon/komoot, OpenStreetMap). Rien n’est enregistré chez nous avant l’ajout au panier.": "Para encontrar tu lugar, tu búsqueda se envía a servicios cartográficos públicos (IGN, Photon/komoot, OpenStreetMap). No guardamos nada antes de que lo añadas a la cesta.",
      "Écrivez le nom en lettres latines": "Escribe el nombre en caracteres latinos",
      "Rue": "Calle",
      "Ville": "Ciudad",
      "Quartier": "Barrio",
      " de large": " de ancho",
      " de large sur la pièce": " de ancho en la pieza",
      "gravure légère": "grabado ligero",
      "gravure moyenne": "grabado medio",
      "gravure dense": "grabado denso",
      " : saisissez-le ci-dessous.": ": escríbelo abajo.",
      "Écrivez le nom du lieu en lettres latines : certains caractères ne peuvent pas être gravés.": "Escribe el nombre del lugar en caracteres latinos: algunos caracteres no se pueden grabar.",
      "Votre texte contient des caractères qui ne peuvent pas être gravés.": "Tu texto contiene caracteres que no se pueden grabar.",
      "Le nom du lieu": "El nombre del lugar",
      "Votre texte": "Tu texto",
      "Les coordonnées": "Las coordenadas",
      " est trop long pour être gravé lisiblement : raccourcissez-le.": " es demasiado largo para grabarse de forma legible: acórtalo.",
      "Classique": "Clásico",
      "Taille-douce": "Aguafuerte",
      "Épure": "Minimalista",
      "épingle cœur": "chincheta de corazón",
      "épingle": "chincheta",
      "cœur": "corazón",
      "sans repère": "sin marcador",
      "Tomette": "Tomette hexagonal",
      "Carreau": "Baldosa cuadrada",
      " percée": " con agujero",
      "Juste les coordonnées GPS ? Notre tomette aux coordonnées gravées →": "¿Solo las coordenadas GPS? Nuestra tomette con coordenadas grabadas →",
      "Réessayer": "Reintentar",
      "Ce point est en pleine eau.": "Este punto está en pleno mar.",
      "Élargir jusqu’à la côte": "Ampliar hasta la costa",
      "Peu de rues ici : la carte sera très épurée.": "Pocas calles aquí: el mapa será muy sobrio.",
      "Élargir": "Ampliar",
      "Pas de date pour l’instant : ajoutez-la à l’étape « Vos mots », ou choisissez « Sans date ».": "Aún no hay fecha: añádela en el paso «Tus palabras» o elige «Sin fecha».",
      "Votre repère est sous le trou de suspension : décalez la carte à l’étape Cadrage, ou retirez le trou.": "Tu marcador está bajo el agujero para colgar: desplaza el mapa en el paso Encuadre o quita el agujero.",
      "Où est né votre souvenir ?": "¿Dónde nació tu recuerdo?",
      "Tomette ou carreau ?": "¿Tomette o baldosa cuadrada?",
      "Cadrez votre carte": "Encuadra tu mapa",
      "Votre gravure": "Tu grabado",
      "Les mots de votre souvenir": "Las palabras de tu recuerdo",
      "Elle est prête.": "Ya está lista.",
      "Précisez l’endroit": "Precisa el sitio",
      "C’est ici": "Es aquí",
      "Suivant": "Siguiente",
      "Personnaliser mes mots": "Personalizar mis palabras",
      "Voir ma carte": "Ver mi mapa",
      "← Retour": "← Volver",
      "Fermer": "Cerrar",
      "Terminer →": "Terminar →",
      "Suivant →": "Siguiente →",
      "Le panier ne répond pas. Réessayez dans un instant.": "La cesta no responde. Inténtalo de nuevo en un momento.",
      "Le panier tarde à répondre. Réessayez dans un instant.": "La cesta tarda en responder. Inténtalo de nuevo en un momento.",
      "Vous modifiez la carte de votre panier : l’ajout remplacera l’ancienne version.": "Estás modificando el mapa de tu cesta: al añadirlo se sustituirá la versión anterior.",
      "Vérification de votre carte…": "Comprobando tu mapa…",
      "Préparation du fichier de gravure…": "Preparando el archivo de grabado…",
      "Enregistrement de votre carte…": "Guardando tu mapa…",
      "Ajout au panier…": "Añadiendo a la cesta…",
      "Vérification du panier…": "Comprobando la cesta…",
      "Votre carte est dans le panier.": "Tu mapa está en la cesta.",
      "L’ajout n’a pas abouti.": "No se ha podido añadir.",
      "Choisissez d’abord votre lieu.": "Primero elige tu lugar.",
      "Votre carte n’a pas pu être préparée.": "No se ha podido preparar tu mapa.",
      "Votre carte se redessine pour cette forme. Réessayez dans un instant.": "Tu mapa se está redibujando para esta forma. Inténtalo de nuevo en un momento.",
      "Lien copié ✓": "Enlace copiado ✓",
      "La création de carte est momentanément indisponible. Réessayez dans quelques minutes.": "La creación de mapas no está disponible por ahora. Inténtalo de nuevo en unos minutos.",
      "Ce lien de carte est incomplet : recommencez à partir de votre lieu.": "Este enlace de mapa está incompleto: vuelve a empezar desde tu lugar.",
    } : null;
    const t = (fr, anglais) => en ? anglais : esp ? (T_ES[fr] ?? fr) : fr;
    const $ = s => root.querySelector(s), $$ = s => [...root.querySelectorAll(s)];
    const R = () => window.CarteRendu, L = () => window.CarteLieu;
    const peintre = window.CartePeintre ? window.CartePeintre.create() : null;
    const preset = config.preset && typeof config.preset === 'object' ? config.preset : {};
    const mobile = matchMedia('(max-width:880px)');
    const statut = $('[data-carte-status]');
    const guide = root.querySelector('[data-carte-guide]');
    const scene = $('[data-carte-scene]'), piece = $('[data-carte-piece]');
    const toile = $('[data-carte-peinture]'), geste = $('[data-carte-geste]'), cible = $('[data-carte-cible]');
    const fond = document.createElement('canvas');                 // la pièce SANS la carte, sous l'aperçu du geste
    fond.className = 'mt-carte-peinture'; fond.setAttribute('aria-hidden', 'true'); fond.hidden = true; piece.insertBefore(fond, geste);
    const prix = new Intl.NumberFormat(config.locale || 'fr', {style: 'currency', currency: config.currency || 'EUR'});
    const variantes = new Map((config.variants || []).map(v => [v.key, v]));

    /* ------------------------------------------------------------------
       L'état
       ------------------------------------------------------------------ */
    const fenetreDe = f => { const p = preset.fenetre && preset.fenetre[f]; return p === 'pleine' || p === 'legende' ? p : FENETRE_DEFAUT[f]; };
    function etatInitial() {
      return {
        v: 1, lieu: null, centre: {dx: 0, dy: 0}, e: null, eAuto: null, eauLieu: 0,
        forme: 'hexa', trou: false,
        style: STYLES.includes(preset.style) ? preset.style : 'classique', eau: 'auto',
        repere: {type: REPERES.includes(preset.repere) ? preset.repere : 'epingle-coeur', taille: 'M'},
        date: {iso: '', fmt: DATES.includes(preset.dateFmt) ? preset.dateFmt : 'points', libre: ''},
        lieuTexte: '', lieuAuto: true,
        coords: {fmt: COORDS.includes(preset.coordsFmt) ? preset.coordsFmt : 'dms'},
        cadeau: {on: false, msg: ''}
      };
    }
    let etat = etatInitial();
    let etape = 0, faites = new Set(), carte = null, sq = null, sqRef = null, verdict = null, eCote = null, geo = null, erreurCarte = '';
    let jetonGravure = 0, jetonPrep = 0, gravureEnCours = null, preparationEnCours = null, recherche = null, precision = null;
    let exemple = {hexa: null, carree: null}, sauverMinuteur = 0, graverMinuteur = 0, peindreMinuteur = 0;
    let editionPanier = null, alerteLatin = '';

    const fenetre = () => fenetreDe(etat.forme);
    const dims = () => PIECES[etat.forme];
    const varianteDe = (forme, trou) => variantes.get(forme + '|' + (trou ? '1' : '0'));
    // le prix au format de la boutique (snippet mtx-prix, rendu par Liquid), sinon Intl
    const prixDe = (forme, trou) => { const v = varianteDe(forme, trou); return v ? (typeof v.prix === 'string' && v.prix ? v.prix : prix.format(v.priceCents / 100)) : ''; };
    function bornesE() {
      const a = etat.eAuto || etat.e || 12;
      return {min: Math.max(4, a / 2.5), max: Math.min(80, a * 3)};
    }

    /* Les textes du gabarit, tels qu'ils seront gravés */
    const texteDate = () => R() ? R().formats.date(etat.date.fmt === 'libre' ? etat.date.libre : etat.date.iso, etat.date.fmt, config.locale) : '';
    const texteCoords = () => (etat.lieu && R()) ? R().formats.coords(etat.lieu.lat, etat.lieu.lon, etat.coords.fmt, config.locale) : '';
    function textesGabarit() {
      return {v: 1, date: texteDate(), iso: ['libre', 'sans'].includes(etat.date.fmt) ? '' : etat.date.iso, fmtDate: etat.date.fmt,
        lieu: etat.lieuTexte.trim(), coords: texteCoords(), fmtCoords: etat.coords.fmt};
    }
    const cssPolice = id => window.CartePeintre ? window.CartePeintre.cssFamille(id) : '';
    /* Les éléments du gabarit (format recette), tailles ajustées EXACTEMENT comme le moteur les réduira
       (CarteRendu.mise, COPIE DE mtLayoutText) : l'aperçu ne ment pas et les réserves couvrent le texte. */
    function elementsTextes(valeurs) {
      const v = valeurs || {lieu: etat.lieuTexte.trim(), date: texteDate(), coords: texteCoords()};
      return R().elementsGabarit(etat.forme, fenetre(), v).map(e => {
        if (e.type !== 'text') return e;
        const m = R().mise(e, etat.forme, {css: cssPolice, minSize: CORPS_MIN / 1.2});
        return {...e, size: m.size, __mise: m};
      });
    }
    function boitesDe(els) {
      const {W, H} = dims();
      return els.map(e => {
        if (e.type === 'text') { const b = e.__mise.boite; return {x: b.x, y: b.y, w: b.w, h: b.h, type: 'text', texte: e.text, gabarit: true}; }
        const l = e.size * 1.2;
        return {x: e.x * W - l / 2, y: e.y * H - 1, w: l, h: 2, type: 'logo', cle: 'custom_filet', gabarit: true};
      });
    }
    function params(textes) {
      return {
        lieu: {lat: etat.lieu.lat, lon: etat.lieu.lon}, centre: {dx: etat.centre.dx, dy: etat.centre.dy},
        e: etat.e, style: etat.style, eau: etat.eau, eauLieu: etat.eauLieu,
        repere: {type: etat.repere.type, taille: 'M'}, fenetre: fenetre(), forme: etat.forme,
        retraitBord: (R() && R().RETRAIT_BORD != null) ? R().RETRAIT_BORD : 1, textes: textes || textesGabarit()
      };
    }
    const tailleFond = f => Math.max(PIECES[f].W, PIECES[f].H) / 1.2;
    /* La recette : la carte (fond, contrats §5.1), puis lieu, date, filet, coordonnées (le Texte du panier
       commence par le lieu, que le mail d'expédition affiche en grand). Les clés __role et __pin sont des
       indications pour le pont : elles ne partent jamais au panier. */
    function recette(els) {
      const elements = (els || elementsTextes()).map(e => { const c = {...e, __role: e._mtcx}; delete c.__mise; delete c._mtcx; return c; });
      return {
        v: 1, shape: etat.forme, hole: etat.trou,
        elements: [{type: 'logo', logo: 'custom_map', x: .5, y: .5, size: r2(tailleFond(etat.forme) * 1e4) / 1e4, lock: true, color: '#4a2616', svg: carte.svg}, ...elements],
        __pin: carte.pointMm ? {x: carte.pointMm.x, y: carte.pointMm.y, type: etat.repere.type} : null
      };
    }

    /* ------------------------------------------------------------------
       Le lien #carte= et le brouillon
       ------------------------------------------------------------------ */
    function encoder() {
      if (!etat.lieu) return '';
      const o = {v: 1, l: [r5(etat.lieu.lat), r5(etat.lieu.lon)], n: (etat.lieu.nom || '').slice(0, 80), cm: (etat.lieu.commune || '').slice(0, 60),
        c: [r2(etat.centre.dx), r2(etat.centre.dy)], e: etat.e ? r1(etat.e) : null, ea: etat.eAuto ? r1(etat.eAuto) : null, w: etat.eauLieu ? r2(etat.eauLieu) : 0,
        f: etat.forme, t: etat.trou ? 1 : 0, s: etat.style, q: etat.eau, r: etat.repere.type,
        d: [etat.date.iso, etat.date.fmt, etat.date.libre.slice(0, 28)], lt: etat.lieuTexte.slice(0, 32), la: etat.lieuAuto ? 1 : 0, k: etat.coords.fmt};
      return versB64(JSON.stringify(o));
    }
    /* Lecture stricte : un lien abîmé ou trafiqué ne pose jamais de valeur farfelue. */
    function decoder(s) {
      if (typeof s !== 'string' || !/^[A-Za-z0-9_-]{8,2000}$/.test(s)) return null;
      let o; try { o = JSON.parse(depuisB64(s)); } catch { return null; }
      if (!o || o.v !== 1 || !Array.isArray(o.l)) return null;
      const lat = +o.l[0], lon = +o.l[1];
      if (!Number.isFinite(lat) || !Number.isFinite(lon) || Math.abs(lat) > 85.05 || Math.abs(lon) > 180) return null;
      const x = etatInitial(), txt = (v, n) => typeof v === 'string' ? v.replace(/[\u0000-\u001f<>]/g, '').slice(0, n) : '';
      x.lieu = {lat, lon, nom: txt(o.n, 80), commune: txt(o.cm, 60)};
      if (Array.isArray(o.c)) x.centre = {dx: borne(+o.c[0] || 0, -200, 200), dy: borne(+o.c[1] || 0, -200, 200)};
      if (Number.isFinite(+o.e) && +o.e > 0) x.e = borne(+o.e, 4, 80);
      if (Number.isFinite(+o.ea) && +o.ea > 0) x.eAuto = borne(+o.ea, 4, 80);
      x.eauLieu = Number.isFinite(+o.w) ? borne(+o.w, 0, 1) : 0;
      if (o.f === 'hexa' || o.f === 'carree') x.forme = o.f;
      x.trou = o.t === 1;
      if (STYLES.includes(o.s)) x.style = o.s;
      if (['auto', 'plein', 'hachures'].includes(o.q)) x.eau = o.q;
      if (REPERES.includes(o.r)) x.repere = {type: o.r, taille: 'M'};
      if (Array.isArray(o.d)) x.date = {iso: /^\d{4}-\d{2}-\d{2}$/.test(o.d[0]) ? o.d[0] : '', fmt: DATES.includes(o.d[1]) ? o.d[1] : 'points', libre: txt(o.d[2], 28)};
      x.lieuTexte = txt(o.lt, 32).toLocaleUpperCase(en ? 'en' : 'fr'); x.lieuAuto = o.la === 1;
      if (COORDS.includes(o.k)) x.coords = {fmt: o.k};
      return x;
    }
    const cleBrouillon = 'mt-carte-brouillon:' + config.id;
    function sauver() {
      clearTimeout(sauverMinuteur);
      sauverMinuteur = setTimeout(() => {
        if (!etat.lieu) return;
        try { localStorage.setItem(cleBrouillon, JSON.stringify({t: Date.now(), s: encoder(), etape, cadeau: etat.cadeau})); } catch { /* navigation privée : l'état reste en mémoire */ }
      }, 400);
    }
    function lireBrouillon() {
      try {
        const b = JSON.parse(localStorage.getItem(cleBrouillon) || 'null');
        if (!b || Date.now() - b.t > BROUILLON_JOURS * 864e5) return null;
        const x = decoder(b.s); if (!x) return null;
        if (b.cadeau && typeof b.cadeau === 'object') x.cadeau = {on: b.cadeau.on === true, msg: typeof b.cadeau.msg === 'string' ? b.cadeau.msg.slice(0, 200) : ''};
        return {etat: x, etape: borne(+b.etape || 0, 0, 5)};
      } catch { return null; }
    }
    function lienCarte() {
      const u = new URL(config.url, location.origin); u.search = ''; u.hash = 'carte=' + encoder();
      return u.href;
    }

    /* ------------------------------------------------------------------
       L'aperçu
       ------------------------------------------------------------------ */
    function dire(texte, erreur = false) { if (statut) { statut.textContent = texte || ''; statut.dataset.error = String(!!erreur); } }
    function attente(oui, texte) {
      const a = $('[data-carte-attente]'); if (!a) return;
      if (texte) $('[data-carte-attente-texte]').textContent = texte;
      a.hidden = !oui;
    }
    async function demo(forme) {
      if (exemple[forme]) return exemple[forme];
      const url = forme === 'carree' ? config.assets.demoCarree : config.assets.demoHexa;
      const r = await fetch(url, {credentials: 'omit'}); if (!r.ok) throw Error('exemple indisponible');
      const svg = await r.text();
      if (!/^\s*<svg\b[^>]*\sdata-mt-fond="carte"/.test(svg)) throw Error('exemple invalide');
      exemple[forme] = svg; return svg;
    }
    /* La recette que l'on peint : la carte du client, ou l'exemple du Vieux-Port tant qu'aucun lieu n'est choisi. */
    /* Une carte n'est peinte (et ne part) que sur la forme pour laquelle elle a été dessinée (contrats, CHANGEMENTS 1). */
    const carteAccordee = () => !!(carte && new RegExp('\\sdata-mtcx-forme="' + etat.forme + '"').test(String(carte.svg).slice(0, 2000)));
    async function recettePeinte() {
      if (carteAccordee()) return recette();
      if (etat.lieu) {
        // la carte de la nouvelle forme se calcule : la pièce et ses mots, sans carte (jamais l'exemple d'un autre lieu)
        const els = elementsTextes().map(e => { const c = {...e}; delete c.__mise; delete c._mtcx; return c; });
        return {v: 1, shape: etat.forme, hole: etat.trou, elements: els};
      }
      const svg = await demo(etat.forme), m = R().marques(svg), tx = (m && m.textes) || {};
      const forme = etat.forme, f = (m && m.fenetre) || FENETRE_DEFAUT[forme];
      const els = R().elementsGabarit(forme, f, {lieu: tx.lieu || '', date: tx.date || '', coords: tx.coords || ''})
        .map(e => e.type === 'text' ? {...e, size: R().mise(e, forme, {css: cssPolice, minSize: CORPS_MIN / 1.2}).size} : e);
      return {v: 1, shape: forme, hole: etat.trou, elements: [{type: 'logo', logo: 'custom_map', x: .5, y: .5, size: tailleFond(forme), lock: true, svg}, ...els]};
    }
    const taillePiece = () => ({w: Math.max(1, Math.round(piece.clientWidth)), h: Math.max(1, Math.round(piece.clientHeight))});
    const dpr = () => Math.min(2, window.devicePixelRatio || 1);
    let peinture = Promise.resolve();
    function peindre() {
      if (!peintre || !R()) return peinture;
      peinture = peinture.then(async () => {
        const {w, h} = taillePiece();
        if (w < 20) return;
        try {
          const rec = await recettePeinte();
          const res = await peintre.render(toile, rec, {width: w, height: h, dpr: dpr()});
          if (res.stale) return;
          geo = res; root.dataset.peint = 'true';
          majAria();
          // la pièce sans la carte : le fond de l'aperçu vivant pendant un geste
          const sansCarte = {...rec, elements: rec.elements.filter(e => e.logo !== 'custom_map')};
          peintre.render(fond, sansCarte, {width: w, height: h, dpr: dpr()}).catch(() => {});
          if (!gesteActif) afficher(modeRepos());
          camera();
        } catch (err) {
          if (!carte) root.dataset.peint = 'false';
          console.warn('[carte] aperçu', err);
        }
      });
      return peinture;
    }
    function peindreBientot(ms = 60) { clearTimeout(peindreMinuteur); peindreMinuteur = setTimeout(peindre, ms); }
    function majAria() {
      const forme = etat.forme === 'hexa' ? t('tomette', 'tomette') : t('carreau', 'square tile');
      // Relecture : l'exemple n'est annoncé que tant qu'AUCUN lieu n'est choisi (pas pendant que la carte du client se dessine).
      const exempleVu = !etat.lieu;
      const nomLieu = exempleVu ? 'Vieux-Port, Marseille' : (etat.lieuTexte || nomCourt());
      const parts = [t('Aperçu de votre ', 'Preview of your ') + forme + ' : ' + t('carte de ', 'map of ') + nomLieu, nomStyle(etat.style), nomRepere(etat.repere.type)];
      const d = exempleVu ? '' : texteDate(); if (d) parts.push(d);
      scene.setAttribute('aria-label', parts.join(', ') + (exempleVu ? t(' (exemple)', ' (example)') : carteAccordee() ? '' : t(' (en cours de dessin)', ' (being drawn)')));
      $('[data-carte-badge]').textContent = exempleVu ? t('Exemple · Vieux-Port, Marseille', 'Example · Vieux-Port, Marseille') : '';
    }

    /* ---- l'aperçu vivant pendant le geste (CarteRendu.apercu, au trait, 2 à 11 ms) ---- */
    let gesteActif = false, offscreen = null, horsPiece = null, frame = 0, modeVue = 'aucune';
    /* Trois façons de montrer la pièce :
       'aucune'  : la gravure peinte (le SVG qui partira au laser) ;
       'fantome' : la gravure peinte, plus la carte HORS de la pièce à 25 % (étape Cadrage, « comme une image ») ;
       'geste'   : pendant un geste, la pièce sans carte et l'aperçu vivant au trait, fantôme compris. */
    function afficher(mode) {
      modeVue = mode;
      toile.hidden = mode === 'geste'; fond.hidden = mode !== 'geste'; geste.hidden = mode === 'aucune';
      if (mode !== 'aucune') demanderGeste();
    }
    const modeRepos = () => (etape === 2 && etat.lieu && sq && carteAccordee()) ? 'fantome' : 'aucune';
    function dessinerGeste() {
      frame = 0;
      if (!sq || !geo || !R() || modeVue === 'aucune') return;
      const {W, H} = dims(), d = dpr(), {w, h} = taillePiece();
      if (geste.width !== Math.round(w * d) || geste.height !== Math.round(h * d)) { geste.width = Math.round(w * d); geste.height = Math.round(h * d); }
      const face = geo.face, sx = face.width / W * d, sy = face.height / H * d, s = Math.max(sx, sy);
      const M = etape === 2 ? Math.ceil(Math.max(face.x, face.y, w - face.x - face.width, h - face.y - face.height) / (face.width / W)) + 2 : 0;
      offscreen = offscreen || document.createElement('canvas');
      const ow = Math.ceil((W + 2 * M) * s), oh = Math.ceil((H + 2 * M) * s);
      if (offscreen.width !== ow || offscreen.height !== oh) { offscreen.width = ow; offscreen.height = oh; }
      const vue = {forme: etat.forme, fenetre: fenetre(), centre: etat.centre, e: etat.e, style: etat.style, eau: etat.eau, eauLieu: etat.eauLieu, repere: etat.repere, pxParMm: s, ox: M * s, oy: M * s};
      if (modeVue === 'geste') { R().apercu(offscreen, sq, vue, {teinte: TEINTE}); decouperReserve(offscreen.getContext('2d'), s, M * s, M * s); }
      else offscreen.getContext('2d').clearRect(0, 0, ow, oh);
      if (M) {
        // Ce qui dépasse, à 25 %, jamais gravé. Hors de la PIÈCE seulement : sur le carreau « carte et
        // légende », le bandeau des mots reste propre.
        horsPiece = horsPiece || document.createElement('canvas');
        if (horsPiece.width !== ow || horsPiece.height !== oh) { horsPiece.width = ow; horsPiece.height = oh; }
        R().apercu(horsPiece, sq, vue, {teinte: TEINTE, fantome: true});
        const hc = horsPiece.getContext('2d');
        hc.save(); hc.setTransform(1, 0, 0, 1, 0, 0); hc.globalCompositeOperation = 'destination-out'; hc.beginPath();
        R().polygoneFenetre(etat.forme, 'pleine').forEach(([x, y], i) => i ? hc.lineTo(M * s + x * s, M * s + y * s) : hc.moveTo(M * s + x * s, M * s + y * s));
        hc.closePath(); hc.fill(); hc.restore();
        offscreen.getContext('2d').drawImage(horsPiece, 0, 0);
      }
      const ctx = geste.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.clearRect(0, 0, geste.width, geste.height);
      ctx.drawImage(offscreen, face.x * d - M * sx, face.y * d - M * sy, (W + 2 * M) * sx, (H + 2 * M) * sy);
    }
    /* La réserve du cartouche, découpée AUSSI dans l'aperçu vivant (enquête du 16/10) : sans elle, pendant un
       geste et jusqu'à l'arrivée de la gravure, les rues passaient sous les mots. Même règle que la gravure
       (reserves.js › rectanglesReserves) pour les lignes du gabarit : boîte englobante agrandie de 5 mm sur
       les côtés et de 2,5 mm en haut et en bas, collée au bord bas à moins de 8 mm, coins de 3 mm.
       Calculée une fois par geste : les mots ne changent pas pendant un geste. */
    let reserveGeste = null;
    function rectReserve() {
      if (!R() || !etat.lieu) return null;
      const g = R().gabarit(etat.forme, fenetre());
      let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
      for (const b of boitesDe(elementsTextes())) { x0 = Math.min(x0, b.x - 5); y0 = Math.min(y0, b.y - 2.5); x1 = Math.max(x1, b.x + b.w + 5); y1 = Math.max(y1, b.y + b.h + 2.5); }
      if (!(x1 > x0) || y0 >= g.bas) return null;      // carreau « carte et légende » : les mots sont sous la fenêtre
      if (g.bas - y1 < 8) y1 = g.bas + 9;               // collée au bord bas, coins arrondis hors de la pièce
      return [x0, y0, x1, y1];
    }
    function decouperReserve(ctx, s, ox, oy) {
      const r = reserveGeste; if (!r) return;
      const x = ox + r[0] * s, y = oy + r[1] * s, w = (r[2] - r[0]) * s, h = (r[3] - r[1]) * s;
      ctx.save(); ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalCompositeOperation = 'destination-out'; ctx.beginPath();
      if (ctx.roundRect) ctx.roundRect(x, y, w, h, 3 * s); else ctx.rect(x, y, w, h);
      ctx.fill(); ctx.restore();
    }
    function demanderGeste() { if (!frame) frame = requestAnimationFrame(dessinerGeste); }
    function debutGeste() {
      if (!sq || !geo) return false;
      reserveGeste = rectReserve();
      gesteActif = true; root.dataset.vue = 'carte'; afficher('geste');
      return true;
    }
    function finGeste() {
      gesteActif = false;
      // l'aperçu reste affiché jusqu'à l'arrivée de la gravure (cahier §2.9) : jamais d'écran vide
      graverBientot(140);
      const ref = sqRef || {e: sq ? sq.e : etat.e, dx: 0, dy: 0};
      if (sq && (etat.e / ref.e > 1.6 || ref.e / etat.e > 1.6 || Math.hypot(etat.centre.dx - ref.dx, etat.centre.dy - ref.dy) > dims().W * .45)) rechargerSquelette();
      majCadrage(); sauver();
    }
    function rechargerSquelette() {
      if (!etat.lieu || !R()) return;
      const p = params(), ref = {e: etat.e, dx: etat.centre.dx, dy: etat.centre.dy};
      R().squelette(p, {canal: 'studio'}).then(s => { if (s) { sq = s; sqRef = ref; if (gesteActif) demanderGeste(); } }).catch(() => { /* la gravure suivra quand même */ });
    }

    /* ------------------------------------------------------------------
       Gravure (worker)
       ------------------------------------------------------------------ */
    // graverMinuteur vaut 0 dès qu'aucune saisie n'attend sa gravure (relecture : ajouter() s'y fie).
    function graverBientot(ms = 450) { clearTimeout(graverMinuteur); graverMinuteur = setTimeout(() => { graverMinuteur = 0; graver(); }, ms); }
    function graver() {
      clearTimeout(graverMinuteur); graverMinuteur = 0;
      if (!etat.lieu || !R() || !etat.e) return Promise.resolve(null);
      const n = ++jetonGravure, els = elementsTextes(), textes = textesGabarit(), p = params(textes);
      attente(true, t('Votre carte se grave…', 'Engraving your map…'));
      const job = R().graver(p, boitesDe(els)).then(async res => {
        if (n !== jetonGravure) return null;
        carte = res; erreurCarte = ''; root.dataset.carte = 'true';
        verdict = res.verdict || verdict;
        await peindre();
        majTout();
        return res;
      }).catch(err => {
        if (n !== jetonGravure) return null;
        erreurCarte = messageErreur(err);
        if (!gesteActif) afficher(modeRepos());
        majTout();
        return null;
      }).finally(() => { if (n === jetonGravure) { attente(false); gravureEnCours = null; } });
      gravureEnCours = job;
      return job;
    }
    function messageErreur(err) {
      const code = err && (err.code || err.message);
      if (code === 'TROP_DETAILLE') return t('Zone trop détaillée : rapprochez-vous.', 'Too much detail here: zoom in.');
      if (code === 'POLE') return t('La carte s’arrête à 85° de latitude : choisissez un lieu plus proche de l’équateur.', 'The map stops at 85° latitude: choose a place closer to the equator.');
      return t('La carte ne peut pas être chargée pour le moment.', 'The map cannot be loaded right now.');
    }
    /* preparationEnCours : la promesse de la préparation en cours (relecture : « Ajouter » touché pendant que
       la carte se dessine attend la carte au lieu de dire « Choisissez d'abord votre lieu »). */
    function preparer() {
      const job = preparer0();
      preparationEnCours = job;
      job.finally(() => { if (preparationEnCours === job) preparationEnCours = null; }).catch(() => { /* preparer0 dit lui-même ses erreurs */ });
      return job;
    }
    async function preparer0() {
      if (!etat.lieu || !R()) return;
      const n = ++jetonPrep;
      attente(true, t('Votre carte se dessine…', 'Drawing your map…'));
      try {
        const res = await R().preparer({lieu: {lat: etat.lieu.lat, lon: etat.lieu.lon}, forme: etat.forme, fenetre: fenetre()});
        if (n !== jetonPrep) return;
        verdict = res.verdict; eCote = res.eCote || null; erreurCarte = '';
        if (res.verdict === 'POLE') { carte = null; sq = null; majTout(); attente(false); return; }
        etat.eAuto = res.e; etat.eauLieu = res.eauLieu || 0;
        if (!etat.e) etat.e = res.e;
        const b = bornesE(); etat.e = borne(etat.e, b.min, b.max);
        sq = res.squelette; sqRef = {e: res.e, dx: 0, dy: 0};
        if (Math.abs(etat.e / res.e - 1) > .4 || Math.hypot(etat.centre.dx, etat.centre.dy) > dims().W * .45) rechargerSquelette();
        await graver();
      } catch (err) {
        if (n !== jetonPrep) return;
        erreurCarte = messageErreur(err); attente(false); majTout();
      }
    }

    /* ------------------------------------------------------------------
       Étape 1 — le lieu
       ------------------------------------------------------------------ */
    function textesLieu() {
      const out = {}, T = (L() && L().textes) || {};
      for (const k of Object.keys(T)) out[k] = en ? T[k][2] : esp && window.MT_CONFIG_I18N ? window.MT_CONFIG_I18N.text(T[k][0], T[k][1]) : T[k][1];
      // Relecture du Lot S (15/10) : le lieu et les fichiers partent chez Shopify dès l'ajout au panier.
      out.vie = t('Pour trouver votre lieu, votre recherche est envoyée à des services cartographiques publics (IGN, Photon/komoot, OpenStreetMap). Rien n’est enregistré chez nous avant l’ajout au panier.',
        'To find your place, your search is sent to public mapping services (IGN, Photon/komoot, OpenStreetMap). Nothing is stored by us before you add it to your basket.');
      return out;
    }
    const nomCourt = () => etat.lieu ? [etat.lieu.nom, etat.lieu.commune && etat.lieu.commune !== etat.lieu.nom ? etat.lieu.commune : ''].filter(Boolean).join(', ') || texteCoordsBrut() : '';
    const texteCoordsBrut = () => etat.lieu && R() ? R().formats.coords(etat.lieu.lat, etat.lieu.lon, 'dms', config.locale) : '';
    function monterLieu() {
      const hote = $('[data-carte-recherche]');
      if (!L() || !R()) return;
      L().config({donnees: config.donnees, locale: config.locale});
      const attenteInput = hote.querySelector('[data-carte-recherche-attente]');
      const valeur = attenteInput ? attenteInput.value : '', avaitFocus = attenteInput && document.activeElement === attenteInput;
      recherche = L().monterRecherche(hote, {locale: config.locale, textes: textesLieu(), valeur, onChoix: choisirLieu, onErreur: () => {}});
      if (avaitFocus) recherche.focus();
      recherche.input.addEventListener('focus', () => R() && R().prechauffer(), {once: true});
      /* Palliatif WebKit (essai Playwright WebKit du 15/10, à signaler au Lot R) : la liste de CarteLieu annule son
         pointerdown pour garder le clavier ouvert, et choisit au « click » ; après Entrée, un appui tactile sur une
         option est arrivé sans aucun click. Si aucun click ne suit le touchend d'une option en 350 ms, on le
         déclenche. Sans effet quand le click arrive. À retirer quand mt-carte-lieu.js choisira au pointerup. */
      const liste = hote.querySelector('.mtcx-lieu-liste');
      if (liste) {
        let clicVu = false;
        liste.addEventListener('click', () => { clicVu = true; }, true);
        liste.addEventListener('touchend', ev => {
          const li = ev.target.closest && ev.target.closest('[data-i]'); if (!li) return;
          clicVu = false;
          setTimeout(() => { if (!clicVu && li.isConnected && !liste.hidden) li.click(); }, 350);
        }, {passive: true});
      }
      if (valeur) recherche.chercher(valeur);
    }
    function etiqueter(lieu) {
      if (!etat.lieuAuto || !L()) return;
      L().libelleGravable(lieu, {locale: config.locale}).then(r => {
        if (!etat.lieuAuto) return;
        etat.lieuTexte = r.texte || '';
        alerteLatin = r.ok ? '' : (r.message || t('Écrivez le nom en lettres latines', 'Write the name in Latin letters'));
        majChamps(); peindreBientot(); graverBientot(300); sauver();
      }).catch(() => {});
    }
    function choisirLieu(lieu) {
      if (!lieu || !Number.isFinite(+lieu.lat) || !Number.isFinite(+lieu.lon)) return;
      etat.lieu = {lat: +lieu.lat, lon: +lieu.lon, nom: lieu.nom || '', commune: lieu.commune || ''};
      etat.centre = {dx: 0, dy: 0}; etat.e = null; etat.eAuto = null;
      root.dataset.lieu = 'true'; root.dataset.cree = 'true'; root.dataset.vue = 'carte';
      const hote = $('[data-carte-precision]');
      hote.hidden = false;
      if (!precision && L()) precision = L().monterPrecision(hote, {lieu: etat.lieu, locale: config.locale, textes: textesLieu(), onChange: surPrecision, onNom: () => {}});
      else if (precision) precision.regler(etat.lieu);
      if (guide && guide.open) guide.dataset.precision = 'true';
      $('[data-carte-choisi]').hidden = false;
      etiqueter(etat.lieu);
      majTout(); preparer(); sauver();
      // Relecture (accessibilité) : l'option choisie quitte le DOM ; le focus ne doit pas tomber sur <body>.
      requestAnimationFrame(() => {
        const a = document.activeElement;
        // (au téléphone, la recherche se masque derrière « Précisez l'endroit » : un champ caché perd aussi le focus)
        if (a && a !== document.body && a.isConnected && a.getClientRects().length && (root.contains(a) || (guide && guide.contains(a)))) return;
        const titre = guide && guide.open ? $('[data-carte-guide-titre]') : $('[data-carte-etape="0"] .mt-carte-etape__titre');
        if (titre) { titre.setAttribute('tabindex', '-1'); titre.focus({preventScroll: true}); }
      });
    }
    let precisionMinuteur = 0;
    function surPrecision(l) {
      if (!l || !etat.lieu) return;
      const bouge = Math.abs(l.lat - etat.lieu.lat) > 1e-6 || Math.abs(l.lon - etat.lieu.lon) > 1e-6;
      etat.lieu = {lat: +l.lat, lon: +l.lon, nom: l.nom || '', commune: l.commune || ''};
      majTout();
      if (bouge) { etat.centre = {dx: 0, dy: 0}; clearTimeout(precisionMinuteur); precisionMinuteur = setTimeout(() => { etat.e = null; preparer(); }, 350); }
      else etiqueter(etat.lieu);
      if (bouge && etat.lieuAuto) etiqueter(etat.lieu);
      sauver();
    }

    /* ------------------------------------------------------------------
       Étapes 2 à 5 — les réglages
       ------------------------------------------------------------------ */
    function changerForme(f) {
      if (!PIECES[f] || f === etat.forme) return;
      etat.forme = f; root.dataset.shape = f;
      etat.centre = etat.lieu && R() ? R().borner({...params(), forme: f, fenetre: fenetreDe(f)}) : {dx: 0, dy: 0};
      envoyerPont('state:change', {shape: etat.forme, hole: etat.trou});
      if (etat.lieu) { peindre(); preparer(); } else peindre();
      majTout(); sauver();
    }
    function changerTrou(oui) {
      etat.trou = !!oui; envoyerPont('state:change', {shape: etat.forme, hole: etat.trou});
      peindreBientot(0); majTout(); sauver();
    }
    function regler(cle, valeur) {
      if (cle === 'style' && STYLES.includes(valeur)) etat.style = valeur;
      else if (cle === 'repere' && REPERES.includes(valeur)) etat.repere = {type: valeur, taille: 'M'};
      else if (cle === 'eau' && ['plein', 'hachures'].includes(valeur)) etat.eau = valeur;
      else return;
      if (cle === 'repere') etat.centre = R().borner(params());
      // Relecture : les vignettes de style suivent le repère et l'eau (leur clé les inclut).
      graver().then(() => { if (etape === 3 && cle !== 'style') vignettes(); });
      majTout(); sauver();
    }
    function changerEchelle(e, {graverAussi = true} = {}) {
      if (!etat.lieu) return;
      const b = bornesE(); etat.e = r1(borne(e, b.min, b.max));
      etat.centre = R().borner(params());
      if (sq && geo) { if (!gesteActif) debutGeste(); demanderGeste(); }
      majCadrage();
      if (graverAussi) { gesteActif = false; finGeste(); }
    }
    const curseurVersE = v => { const b = bornesE(); return b.min * Math.pow(b.max / b.min, v / 1000); };
    const eVersCurseur = e => { const b = bornesE(); return Math.round(1000 * Math.log(e / b.min) / Math.log(b.max / b.min)); };
    function nomCran(e) {
      const a = etat.eAuto || e, k = e / a;
      return k < .78 ? t('Rue', 'Street') : k > 1.5 ? t('Ville', 'Town') : t('Quartier', 'Neighbourhood');
    }
    function largeurKm(e) {
      const km = dims().W * e / 1000;
      return (km < 10 ? km.toFixed(1) : String(Math.round(km))).replace('.', en ? '.' : ',') + ' km';
    }
    function majCadrage() {
      const c = $('[data-carte-echelle]');
      if (!etat.e) return;
      c.value = String(borne(eVersCurseur(etat.e), 0, 1000));
      const texte = nomCran(etat.e) + ', ' + largeurKm(etat.e) + t(' de large', ' wide');
      c.setAttribute('aria-valuetext', texte);
      $('[data-carte-largeur]').textContent = largeurKm(etat.e) + t(' de large sur la pièce', ' across the tile');
      const a = etat.eAuto || etat.e, crans = {rue: a * .6, quartier: a, ville: a * 2};
      for (const b of $$('[data-carte-cran]')) b.setAttribute('aria-pressed', String(Math.abs(etat.e / crans[b.dataset.carteCran] - 1) < .08));
      majResumes();
    }

    /* Les vignettes de style : la MÊME carte, gravée dans chaque style (canal à part, sans gêner la principale) */
    let vignettesCle = '';
    async function vignettes() {
      if (!etat.lieu || !R() || !etat.e) return;
      const p0 = params(), cle = JSON.stringify([p0.lieu, p0.centre, p0.e, p0.forme, p0.repere, p0.eau]);
      if (cle === vignettesCle) return;
      vignettesCle = cle;
      const els = elementsTextes(), boites = boitesDe(els);
      for (const style of STYLES) {
        if (vignettesCle !== cle) return;
        try {
          const res = await R().graver({...p0, style}, boites, {canal: 'vignette-' + style});
          if (vignettesCle !== cle) return;
          await dessinerVignette($('[data-carte-vignette="' + style + '"]'), res.svg);
          const j = R().jauge(res.encre), mots = {legere: t('gravure légère', 'light engraving'), moyenne: t('gravure moyenne', 'medium engraving'), dense: t('gravure dense', 'dense engraving')};
          const jauge = $('[data-carte-jauge="' + style + '"]'); jauge.textContent = mots[j] || '';
          const input = $('[data-carte-style][value="' + style + '"]'); if (input) input.setAttribute('aria-label', nomStyle(style) + ', ' + (mots[j] || ''));
        } catch { /* la vignette garde l'image précédente */ }
      }
    }
    function dessinerVignette(canvas, svg) {
      return new Promise(resolve => {
        if (!canvas) { resolve(); return; }
        const {W, H} = dims(), d = dpr(), cw = canvas.clientWidth || 100, ch = Math.round(cw / 1.2);
        canvas.width = Math.round(cw * d); canvas.height = Math.round(ch * d);
        const im = new Image();
        im.onload = () => {
          // Gros plan ×2 autour du repère (60 × 50 mm de la pièce) : à cette taille, les trois styles se distinguent.
          const ctx = canvas.getContext('2d'), zoom = 2, k = canvas.width / W * zoom;
          const pt = (carte && carte.pointMm) || {x: W / 2, y: H / 2};
          const cx = borne(pt.x, W / (2 * zoom), W - W / (2 * zoom)), cy = borne(pt.y - 4, canvas.height / k / 2, H - canvas.height / k / 2);
          const x = canvas.width / 2 - cx * k, y = canvas.height / 2 - cy * k;
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.fillStyle = '#eec3a2'; ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(im, x, y, W * k, H * k); resolve();
        };
        im.onerror = () => resolve();
        im.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(String(svg).replace(/\sdata-mtcx-[a-z]+="[^"]*"/g, '').replace(/fill="#4a2616"/, 'fill="#6b3a1f"'));
      });
    }

    /* Vos mots */
    function majChamps() {
      const d = $('[data-carte-date]'), f = $('[data-carte-date-format]'), lib = $('[data-carte-date-libre]');
      if (d.value !== etat.date.iso) d.value = etat.date.iso;
      f.value = etat.date.fmt;
      lib.hidden = etat.date.fmt !== 'libre'; d.disabled = ['libre', 'sans'].includes(etat.date.fmt);
      // les formats se montrent avec LA date du client, sinon avec celle de l'exemple
      const iso = etat.date.iso || '2024-09-14';
      for (const opt of f.options) if (['points', 'maison', 'long'].includes(opt.value)) { const v = R().formats.date(iso, opt.value, config.locale); if (opt.textContent !== v) opt.textContent = v; }
      if (lib.value !== etat.date.libre) lib.value = etat.date.libre;
      const lt = $('[data-carte-lieu-texte]'); if (document.activeElement !== lt && lt.value !== etat.lieuTexte) lt.value = etat.lieuTexte;
      $('[data-carte-coords-format]').value = etat.coords.fmt;
      $('[data-carte-coords-valeur]').textContent = texteCoords();
    }
    function apresTexte() { majResumes(); peindreBientot(40); graverBientot(500); verifierMots(); sauver(); }
    /* Les contrôles de gravabilité des mots : polices du gabarit (D35) et corps minimal (D10). */
    function problemesMots() {
      if (!R()) return [];
      const out = [], tx = textesGabarit();
      if (alerteLatin && !tx.lieu) out.push(alerteLatin + t(' : saisissez-le ci-dessous.', ': type it below.'));
      for (const [cle, v] of [['lieu', tx.lieu], ['date', tx.date]]) {
        if (v && !R().formats.gravable(v)) out.push(cle === 'lieu' ? t('Écrivez le nom du lieu en lettres latines : certains caractères ne peuvent pas être gravés.', 'Write the place name in Latin letters: some characters cannot be engraved.') : t('Votre texte contient des caractères qui ne peuvent pas être gravés.', 'Your text contains characters that cannot be engraved.'));
      }
      const {W} = dims();
      for (const e of elementsTextes()) {
        if (e.type !== 'text') continue;
        const m = e.__mise;
        if (m.boite.w > m.wFrac * W + 0.05) out.push((e._mtcx === 'lieu' ? t('Le nom du lieu', 'The place name') : e._mtcx === 'date' ? t('Votre texte', 'Your text') : t('Les coordonnées', 'The coordinates')) + t(' est trop long pour être gravé lisiblement : raccourcissez-le.', ' is too long to be engraved legibly: please shorten it.'));
      }
      return [...new Set(out)];
    }
    function verifierMots() { const p = problemesMots(); $('[data-carte-mots-alerte]').textContent = p.join(' '); return p; }

    /* ------------------------------------------------------------------
       Les étapes : affichage, résumés, navigation
       ------------------------------------------------------------------ */
    const nomStyle = s => ({classique: t('Classique', 'Classic'), 'taille-douce': t('Taille-douce', 'Etching'), epure: t('Épure', 'Minimal')}[s] || s);
    const nomRepere = r => ({'epingle-coeur': t('épingle cœur', 'heart pin'), epingle: t('épingle', 'pin'), coeur: t('cœur', 'heart'), aucun: t('sans repère', 'no marker')}[r] || r);
    const nomForme = f => f === 'hexa' ? t('Tomette', 'Hexagonal tomette') : t('Carreau', 'Square tile');
    function majResumes() {
      const res = [
        etat.lieu ? nomCourt() + ' · ' + texteCoordsBrut() : '',
        nomForme(etat.forme) + (etat.trou ? t(' percée', ' with hole') : '') + ' · ' + prixDe(etat.forme, etat.trou),
        etat.e ? nomCran(etat.e) + ' · ' + largeurKm(etat.e) + t(' de large', ' wide') : '',
        nomStyle(etat.style) + ' · ' + nomRepere(etat.repere.type),
        [etat.lieuTexte, texteDate()].filter(Boolean).join(' · '),
        prixDe(etat.forme, etat.trou)
      ];
      res.forEach((v, i) => { const p = $('[data-carte-resume="' + i + '"]'); if (p) p.textContent = faites.has(i) || (i === 1 && etat.lieu) ? v : ''; });
      $('[data-carte-recap]').textContent = [etat.lieuTexte || nomCourt(), nomStyle(etat.style), nomForme(etat.forme) + (etat.trou ? t(' percée', ' with hole') : '')].filter(Boolean).join(' · ');
      for (const el of $$('[data-carte-prix-pret]')) el.textContent = prixDe(etat.forme, etat.trou);
      const fp = $('[data-carte-flottant-prix]'); if (fp && root.dataset.pret === 'true') fp.textContent = prixDe(etat.forme, etat.trou);
    }
    /* Pleine mer, pôles : la tomette aux seules coordonnées GPS (produit publié seulement, cahier §1.6). */
    function lienGps() {
      const u = typeof config.gpsUrl === 'string' && /^\/[a-z0-9\/-]+$/i.test(config.gpsUrl) ? config.gpsUrl : '';
      return u ? '<a class="mt-carte-lien" href="' + u + '">' + t('Juste les coordonnées GPS ? Notre tomette aux coordonnées gravées →', 'Just the GPS coordinates? Our engraved coordinates tile →') + '</a>' : '';
    }
    function majVerdict() {
      const msgs = [];
      let bouton = '';
      if (erreurCarte) { msgs.push(erreurCarte); bouton = '<button type="button" data-carte-reessayer>' + t('Réessayer', 'Try again') + '</button>'; }
      else if (verdict === 'MER') { msgs.push(t('Ce point est en pleine eau.', 'This spot is out at sea.')); if (eCote) bouton = '<button type="button" data-carte-elargir="' + eCote + '">' + t('Élargir jusqu’à la côte', 'Widen to the coast') + '</button>'; else bouton = lienGps(); }
      else if (verdict === 'VIDE') { msgs.push(t('Peu de rues ici : la carte sera très épurée.', 'Few streets here: the map will be very sparse.')); if (etat.e && etat.e < bornesE().max - .5) bouton = '<button type="button" data-carte-elargir="' + r1(Math.min(bornesE().max, etat.e * 1.6)) + '">' + t('Élargir', 'Widen') + '</button>'; }
      else if (verdict === 'POLE') { msgs.push(t('La carte s’arrête à 85° de latitude : choisissez un lieu plus proche de l’équateur.', 'The map stops at 85° latitude: choose a place closer to the equator.')); bouton = lienGps(); }
      const html = msgs.length ? msgs.map(m => m.replace(/[&<>]/g, c => ({'&': '&amp;', '<': '&lt;', '>': '&gt;'}[c]))).join(' ') + ' ' + bouton : '';
      for (const el of [$('[data-carte-verdict]'), $('[data-carte-verdict-lieu]')]) if (el && el.innerHTML !== html) el.innerHTML = html;
    }
    function majEtapes() {
      for (const s of $$('[data-carte-etape]')) {
        const i = +s.dataset.carteEtape;
        s.dataset.etat = i === etape ? 'active' : faites.has(i) ? 'faite' : 'avenir';
        const corps = $('[data-carte-corps="' + i + '"]'); if (corps) corps.hidden = i !== etape;
        const mod = s.querySelector('[data-carte-aller]'); if (mod) mod.hidden = i === etape || !etat.lieu || !faites.has(i);
      }
      root.dataset.etape = String(etape);
      const nomChoisi = $('[data-carte-choisi-nom]'); if (nomChoisi) nomChoisi.textContent = etat.lieu ? '📍 ' + nomCourt() : '';
      $('[data-carte-choisi]').hidden = !etat.lieu || $('[data-carte-precision]').hidden;
      const cadrage = etape === 2 && !!etat.lieu;
      cible.hidden = !cadrage; $('[data-carte-zoom]').hidden = !cadrage;
      $('[data-carte-suivant="0"]').disabled = !etat.lieu || verdict === 'POLE';
      const pret = !!(carteAccordee() && etat.lieu && (faites.has(4) || etape === 5));
      root.dataset.pret = String(pret);
      $('[data-carte-ajout-page]').hidden = !pret;
      const eauBloc = $('[data-carte-eau]'), stats = carte && carte.stats;
      eauBloc.hidden = !(etat.style === 'classique' && carte && (carte.eau === 'plein' || carte.eau === 'hachures') && ((stats && stats.fracEau > .004) || etat.eauLieu > .02));
      if (!eauBloc.hidden) { const v = etat.eau !== 'auto' ? etat.eau : carte.eau; for (const r of $$('[data-carte-eau-choix]')) r.checked = r.value === v; }
      for (const r of $$('[data-carte-forme]')) r.checked = r.value === etat.forme;
      $('[data-carte-trou]').checked = etat.trou;
      for (const r of $$('[data-carte-style]')) r.checked = r.value === etat.style;
      for (const r of $$('[data-carte-repere]')) r.checked = r.value === etat.repere.type;
      const ca = $('[data-carte-cadeau]'); if (ca) { ca.checked = etat.cadeau.on; $('[data-carte-cadeau-bloc]').hidden = !etat.cadeau.on; const m = $('[data-carte-cadeau-msg]'); if (m.value !== etat.cadeau.msg) m.value = etat.cadeau.msg; }
      // Étape Prête : rappels (jamais bloquants pour la date), blocages de gravabilité
      const pa = $('[data-carte-pret-alerte]');
      if (pa) {
        const p = problemesMots(), rappels = [...p];
        if (!p.length && etat.date.fmt !== 'sans' && !texteDate()) rappels.push(t('Pas de date pour l’instant : ajoutez-la à l’étape « Vos mots », ou choisissez « Sans date ».', 'No date yet: add one at the “Your words” step, or choose “No date”.'));
        if (repereSousTrou()) rappels.push(t('Votre repère est sous le trou de suspension : décalez la carte à l’étape Cadrage, ou retirez le trou.', 'Your marker sits under the hanging hole: move the map at the Framing step, or remove the hole.'));
        pa.textContent = rappels.join(' ');
      }
      if (editionPanier) $('[data-carte-etat-panier]').textContent = editionPanier;
      // Relecture : « Elle est prête. » seulement quand la carte est là ; avant, on dit qu'elle se dessine.
      const qPret = $('.mt-carte-question--pret');
      if (qPret) { qPret.dataset.texte = qPret.dataset.texte || qPret.textContent; const v = etat.lieu && !carteAccordee() && !erreurCarte ? t('Votre carte se dessine…', 'Drawing your map…') : qPret.dataset.texte; if (qPret.textContent !== v) qPret.textContent = v; }
    }
    function majTout() { majEtapes(); majResumes(); majVerdict(); majCadrage(); majChamps(); majAria(); majFlottant(); if (guide && guide.open) phaseGuide(etape, false); }
    function aller(n, {focus = true} = {}) {
      n = borne(n, 0, 5);
      if (n > 0 && !etat.lieu) return;
      if (n > etape) for (let i = etape; i < n; i++) faites.add(i);
      else if (n < etape) faites.add(etape);
      etape = n;
      if (n >= 1) { chargerPont(); if (root.dataset.vue === 'photo') voirCreation(); }
      if (n === 3) vignettes();
      if (n === 5) prechaufferMoteur();
      majTout();
      if (guide && guide.open) phaseGuide(n, focus);
      else if (focus && !mobile.matches) {
        const s = $('[data-carte-etape="' + n + '"]');
        if (s) { const r = s.getBoundingClientRect(); if (r.top < 80 || r.top > innerHeight * .6) s.scrollIntoView({block: 'start', behavior: reduit() ? 'auto' : 'smooth'}); s.querySelector('.mt-carte-etape__titre')?.setAttribute('tabindex', '-1'); s.querySelector('.mt-carte-etape__titre')?.focus({preventScroll: true}); }
      }
      if (n === 0 && etat.lieu && !precision && L()) {
        const hote = $('[data-carte-precision]'); hote.hidden = false;
        precision = L().monterPrecision(hote, {lieu: etat.lieu, locale: config.locale, textes: textesLieu(), onChange: surPrecision, onNom: () => {}});
        if (guide && guide.open) phaseGuide(0, false);
      }
      if (!gesteActif) afficher(modeRepos());
      sauver();
    }

    /* ------------------------------------------------------------------
       Le guide plein écran (téléphone) — squelette de mtp-photo-studio.js
       ------------------------------------------------------------------ */
    let guideMontages = [], guideFocus = null, guideBody = '', guideHtml = '';
    const TITRES = [t('Où est né votre souvenir ?', 'Where did your story begin?'), t('Tomette ou carreau ?', 'Tomette or square tile?'), t('Cadrez votre carte', 'Frame your map'), t('Votre gravure', 'Your engraving'), t('Les mots de votre souvenir', 'The words of your memory'), t('Elle est prête.', 'It’s ready.')];
    function guideViewport() {
      if (!guide || !guide.open) return;
      const v = window.visualViewport;
      guide.style.setProperty('--carte-guide-height', ((v && v.height) || innerHeight) + 'px');
      guide.style.setProperty('--carte-guide-top', ((v && v.offsetTop) || 0) + 'px');
      if (guide.dataset.mot) requestAnimationFrame(camera);
    }
    function phaseGuide(n, focus) {
      guide.dataset.etape = String(n);
      guide.dataset.precision = String(n === 0 && !!etat.lieu && !$('[data-carte-precision]').hidden);
      $('[data-carte-guide-compte]').textContent = (n + 1) + ' / 6';
      $('[data-carte-guide-titre]').textContent = n === 0 && etat.lieu ? t('Précisez l’endroit', 'Pinpoint the spot') : n === 5 && etat.lieu && !carteAccordee() && !erreurCarte ? t('Votre carte se dessine…', 'Drawing your map…') : TITRES[n];
      for (const b of $$('[data-carte-guide-etape]')) {
        const i = +b.dataset.carteGuideEtape;
        if (i === n) b.setAttribute('aria-current', 'step'); else b.removeAttribute('aria-current');
        b.disabled = i > 0 && !etat.lieu;
      }
      const suivant = $('[data-carte-guide-suivant]');
      suivant.hidden = n === 5;
      suivant.disabled = n === 0 && (!etat.lieu || verdict === 'POLE');
      suivant.innerHTML = [t('C’est ici', 'This is the place'), t('Suivant', 'Next'), t('Suivant', 'Next'), t('Personnaliser mes mots', 'Personalise my words'), t('Voir ma carte', 'See my map')][n] + ' <span aria-hidden="true">→</span>';
      $('[data-carte-guide-retour]').textContent = n ? t('← Retour', '← Back') : t('Fermer', 'Close');
      if (guide.dataset.phase !== String(n)) { guide.dataset.phase = String(n); $('[data-carte-guide-contenu]').scrollTop = 0; }
      if (n !== 4 && guide.dataset.mot) finMots(false);
      if (focus) {
        if (n === 4) debutMots('date', true);
        else $('[data-carte-guide-titre]').focus({preventScroll: true});
      }
      requestAnimationFrame(() => { guideViewport(); redimensionner(); });
    }
    function ouvrirGuide({focusRecherche = false} = {}) {
      if (!guide || !mobile.matches) {
        // ordinateur : le studio est déjà là ; on y va
        aller(etat.lieu ? etape : 0, {focus: !focusRecherche});
        $('[data-carte-etapes]').scrollIntoView({block: 'start', behavior: reduit() ? 'auto' : 'smooth'});
        if (focusRecherche) champRecherche()?.focus({preventScroll: true});
        return;
      }
      if (!guide.open) {
        guideFocus = document.activeElement; guideBody = document.body.style.overflow; guideHtml = document.documentElement.style.overflow;
        const monter = (node, hote) => { const m = document.createComment('carte-guide-position'); node.before(m); guideMontages.push({node, m}); hote.append(node); };
        monter($('[data-carte-stage]'), $('[data-carte-guide-apercu]'));
        for (let i = 0; i < 6; i++) monter($('[data-carte-corps="' + i + '"]'), $('[data-carte-guide-contenu]'));
        document.body.style.overflow = 'hidden'; document.documentElement.style.overflow = 'hidden';
        root.dataset.mobileGuide = 'true'; guide.showModal();
      }
      majTout(); phaseGuide(etape, !focusRecherche);
      // Dans le MÊME geste que l'appui : sinon le clavier iOS ne s'ouvre pas (cahier §5.4).
      if (focusRecherche && etape === 0) champRecherche()?.focus({preventScroll: true});
      guideViewport(); majFlottant();
    }
    function fermerGuide() {
      if (!guide || !guide.open) return;
      finMots(false);
      guide.close();
      for (const {node, m} of guideMontages) m.replaceWith(node);
      guideMontages = [];
      document.body.style.overflow = guideBody; document.documentElement.style.overflow = guideHtml;
      root.dataset.mobileGuide = 'false';
      piece.style.transform = '';
      majTout(); sauver();
      requestAnimationFrame(() => { redimensionner(); if (guideFocus && guideFocus.isConnected) guideFocus.focus({preventScroll: true}); });
    }
    const champRecherche = () => $('[data-carte-recherche] input');

    /* Vos mots, un champ par écran ; la caméra zoome sur la ligne (COPIE DE guideTextCamera, photo) */
    const MOTS = ['date', 'lieu', 'coords'];
    function debutMots(champ, focus) {
      if (!guide || !guide.open || etape !== 4) return;
      guide.dataset.mot = champ;
      for (const b of $$('[data-carte-mot]')) { if (b.dataset.carteMot === champ) b.setAttribute('aria-current', 'step'); else b.removeAttribute('aria-current'); }
      $('[data-carte-mot-suivant]').textContent = champ === 'coords' ? t('Terminer →', 'Done →') : t('Suivant →', 'Next →');
      if (focus) {
        const cible2 = champ === 'date' ? (etat.date.fmt === 'libre' ? $('[data-carte-date-libre]') : $('[data-carte-date]').disabled ? $('[data-carte-date-format]') : $('[data-carte-date]')) : champ === 'lieu' ? $('[data-carte-lieu-texte]') : $('[data-carte-coords-format]');
        cible2.focus({preventScroll: true});
      }
      guideViewport(); requestAnimationFrame(camera);
    }
    function finMots(retour = true) {
      if (!guide || !guide.dataset.mot) return;
      delete guide.dataset.mot; piece.style.transform = '';
      document.activeElement && document.activeElement.blur && guide.contains(document.activeElement) && document.activeElement.blur();
      if (retour) { phaseGuide(4, false); $('[data-carte-guide-titre]').focus({preventScroll: true}); }
      requestAnimationFrame(() => { guideViewport(); redimensionner(); });
    }
    function motSuivant() {
      const i = MOTS.indexOf(guide.dataset.mot);
      if (i >= 0 && i < MOTS.length - 1) debutMots(MOTS[i + 1], true);
      else { finMots(false); aller(5); }
    }
    function camera() {
      if (!guide || !guide.open || !guide.dataset.mot || !geo || !R()) return;
      const champ = guide.dataset.mot, el = elementsTextes().find(e => e._mtcx === champ), hote = $('[data-carte-guide-apercu]');
      piece.style.transform = 'none';
      if (!el) return;
      const {W, H} = dims(), b = el.__mise.boite, f = geo.face;
      const px = f.x + (b.x + b.w / 2) * f.width / W, py = f.y + (b.y + b.h / 2) * f.height / H, encre = b.w * f.width / W;
      const sr = piece.getBoundingClientRect(), hr = hote.getBoundingClientRect();
      if (!hr.width || !hr.height) return;
      const zoom = borne((hr.width - 38) / Math.max(100, encre), 1, 3.1);
      const x = hr.width / 2 - (sr.left - hr.left) - zoom * px, y = hr.height * .54 - (sr.top - hr.top) - zoom * py;
      piece.style.transform = 'translate(' + x + 'px,' + y + 'px) scale(' + zoom + ')';
    }

    /* Le bouton flottant (IntersectionObserver), comme la photo */
    function majFlottant() {
      const ancre = $('[data-carte-ouvrir]'), flottant = $('[data-carte-flottant]');
      if (!ancre || !flottant) return;
      const r = ancre.getBoundingClientRect(), masque = !!document.querySelector('dialog[open]');
      // visible dès que le bouton d'entrée n'est pas à l'écran (au-dessus, ou encore sous le pli d'un petit téléphone)
      flottant.hidden = masque || !mobile.matches || r.width === 0 || (r.bottom > 0 && r.top < innerHeight);
      const fp = $('[data-carte-flottant-prix]');
      if (fp && root.dataset.pret === 'true') fp.textContent = prixDe(etat.forme, etat.trou);
    }

    /* ------------------------------------------------------------------
       Le panier, par le pont (mt-carte-produit.js)
       ------------------------------------------------------------------ */
    let pont = null, pontSession = null, pontPret = null, demandeEnCours = null, derniereDemande = null;
    function envoyerPont(type, data = {}) {
      if (!pontSession && type !== 'ready') return;
      root.dispatchEvent(new CustomEvent('mt-carte:commerce', {detail: {channel: CHANNEL, type, session: pontSession, ...data}}));
    }
    function chargerPont() {
      if (pont) return pont;
      pont = new Promise((resolve, reject) => {
        let fini = false;
        pontPret = () => { if (!fini) { fini = true; resolve(true); } };
        const s = document.createElement('script');
        s.src = config.assets.produit; s.defer = true;
        s.onerror = () => { fini = true; pont = null; reject(Error(t('Le panier ne répond pas. Réessayez dans un instant.', 'The basket is not responding. Please try again in a moment.'))); };
        document.head.appendChild(s);
        setTimeout(() => { if (!fini) { fini = true; pont = null; reject(Error(t('Le panier tarde à répondre. Réessayez dans un instant.', 'The basket is slow to respond. Please try again in a moment.'))); } }, 20000);
      });
      return pont;
    }
    function prechaufferMoteur() {
      chargerPont().then(() => {
        const go = () => envoyerPont('engine:warm');
        if (window.requestIdleCallback) requestIdleCallback(go, {timeout: 3000}); else setTimeout(go, 1200);
      }).catch(() => {});
    }
    root.addEventListener('mt-carte:studio', ev => {
      const m = ev.detail;
      if (!m || m.channel !== CHANNEL) return;
      if (m.type === 'init') {
        const premiere = !pontSession;
        pontSession = m.session;
        if (premiere) envoyerPont('ready', {requestId: uuid().replace(/[^A-Za-z0-9_-]/g, '')});
        if (pontPret) pontPret();
        return;
      }
      if (m.session !== pontSession) return;
      if (m.type === 'edit:result') {
        editionPanier = m.ok ? t('Vous modifiez la carte de votre panier : l’ajout remplacera l’ancienne version.', 'You are editing the map in your basket: adding it will replace the previous version.') : '';
        // Relecture (D18) : l'emballage cadeau déjà au panier se retrouve coché, avec son message (il suivra la nouvelle version).
        if (m.ok && m.cadeau && m.cadeau.on === true) etat.cadeau = {on: true, msg: typeof m.cadeau.msg === 'string' ? m.cadeau.msg.slice(0, 200) : ''};
        majEtapes();
      } else if (m.type === 'cart:progress') {
        const textes = {prepare: t('Vérification de votre carte…', 'Checking your map…'), render: t('Préparation du fichier de gravure…', 'Preparing the engraving file…'), save: t('Enregistrement de votre carte…', 'Saving your map…'), cart: t('Ajout au panier…', 'Adding to your basket…'), verify: t('Vérification du panier…', 'Checking your basket…')};
        messagePanier(textes[m.stage] || '');
      } else if (m.type === 'cart:result') {
        if (!demandeEnCours || m.requestId !== demandeEnCours.id) return;
        if (m.ok) { messagePanier(t('Votre carte est dans le panier.', 'Your map is in your basket.')); try { localStorage.removeItem(cleBrouillon); } catch { /* rien */ } }
        else { messagePanier(m.error || t('L’ajout n’a pas abouti.', 'The item could not be added.'), true); $('[data-carte-panier-actions]').hidden = false; demandeEnCours.echec = true; }
      }
    });
    const dialoguePanier = $('[data-carte-panier]');
    function messagePanier(texte, erreur = false) {
      const m = $('[data-carte-panier-message]'); m.textContent = texte; m.dataset.error = String(erreur);
      const e = $('[data-carte-etat-panier]'); e.textContent = erreur ? texte : ''; e.dataset.error = String(erreur);
    }
    function verifierAvantPanier() {
      if (!etat.lieu) return t('Choisissez d’abord votre lieu.', 'Choose your place first.');
      if (!carte) return erreurCarte || t('Votre carte n’a pas pu être préparée.', 'Your map could not be prepared.');
      if (!carteAccordee()) return t('Votre carte se redessine pour cette forme. Réessayez dans un instant.', 'Your map is being redrawn for this shape. Please try again in a moment.');
      if (verdict === 'POLE') return messageErreur({code: 'POLE'});
      const p = problemesMots(); if (p.length) return p[0];
      if (repereSousTrou()) return t('Votre repère est sous le trou de suspension : décalez la carte à l’étape Cadrage, ou retirez le trou.', 'Your marker sits under the hanging hole: move the map at the Framing step, or remove the hole.');
      return '';
    }
    function repereSousTrou() {
      if (!etat.trou || !carte || !carte.pointMm || etat.repere.type === 'aucun') return false;
      const k = etat.forme === 'hexa' ? .87 : 1, p = carte.pointMm;
      const pts = etat.repere.type === 'coeur' ? [[p.x, p.y], [p.x - 3.7 * k, p.y], [p.x + 3.7 * k, p.y]] : [[p.x, p.y], [p.x, p.y - 4 * k], [p.x, p.y - 6 * k]];
      return TROUS[etat.forme].some(([cx, cy]) => pts.some(([x, y]) => Math.hypot(x - cx, y - cy) < 4));
    }
    /* La carte se dessine encore (lien rouvert, « Modifier » depuis le panier, autre forme) : on l'attend,
       dialogue ouvert, au lieu de refuser (relecture). 90 s au plus, puis le message d'erreur habituel. */
    async function attendreCarte() {
      const fin = Date.now() + 90000;
      while (etat.lieu && !carteAccordee() && !erreurCarte && Date.now() < fin && (preparationEnCours || gravureEnCours || graverMinuteur)) {
        await Promise.race([preparationEnCours || gravureEnCours || new Promise(r => setTimeout(r, 300)), new Promise(r => setTimeout(r, 1000))]).catch(() => {});
      }
    }
    async function ajouter() {
      if (etat.lieu && !carteAccordee() && !erreurCarte && (preparationEnCours || gravureEnCours || graverMinuteur)) {
        if (!dialoguePanier.open) dialoguePanier.showModal();
        $('[data-carte-panier-actions]').hidden = true; messagePanier(t('Votre carte se dessine…', 'Drawing your map…'));
        await attendreCarte();
        if (!carteAccordee()) dialoguePanier.close();
      }
      const probleme = verifierAvantPanier();
      if (probleme) { if (etape !== 5) aller(5); const pa = $('[data-carte-pret-alerte]'); pa.textContent = probleme; dire(probleme, true); return; }
      try {
        if (!dialoguePanier.open) dialoguePanier.showModal();
        $('[data-carte-panier-actions]').hidden = true; messagePanier(t('Vérification de votre carte…', 'Checking your map…'));
        if (gravureEnCours) await gravureEnCours;                // la dernière saisie est gravée avant de partir
        if (graverMinuteur) { clearTimeout(graverMinuteur); graverMinuteur = 0; await graver(); }
        if (!carte) throw Error(erreurCarte || t('Votre carte n’a pas pu être préparée.', 'Your map could not be prepared.'));
        await chargerPont();
        if (!pontSession) throw Error(t('Le panier ne répond pas. Réessayez dans un instant.', 'The basket is not responding. Please try again in a moment.'));
        const rec = recette(), cle = JSON.stringify([rec, etat.cadeau]);
        // Une même composition rejouée après un échec garde son identifiant : le pont ne crée jamais de doublon.
        const id = derniereDemande && derniereDemande.cle === cle ? derniereDemande.id : uuid().replace(/[^A-Za-z0-9_-]/g, '');
        derniereDemande = {cle, id}; demandeEnCours = {id};
        envoyerPont('cart:add', {requestId: id, recipe: rec, cadeau: etat.cadeau.on && $('[data-carte-cadeau]') ? {on: true, msg: etat.cadeau.msg} : null /* F1 16/10 : sans case à l'écran, jamais d'emballage (brouillon ou panier anciens compris) */, etat: encoder()});
      } catch (err) {
        messagePanier(err.message, true); $('[data-carte-panier-actions]').hidden = false;
      }
    }

    /* ------------------------------------------------------------------
       Les gestes du cadrage
       ------------------------------------------------------------------ */
    const pointeurs = new Map();
    let base = null;
    const pxParMm = () => geo ? {x: geo.face.width / dims().W, y: geo.face.height / dims().H} : {x: 4, y: 4};
    function bornerCentre() { etat.centre = R().borner(params()); }
    cible.addEventListener('pointerdown', ev => {
      if (etape !== 2 || !etat.lieu || !sq) return;
      cible.setPointerCapture && cible.setPointerCapture(ev.pointerId);
      pointeurs.set(ev.pointerId, {x: ev.clientX, y: ev.clientY});
      base = {centre: {...etat.centre}, e: etat.e, pts: new Map([...pointeurs].map(([k, v]) => [k, {...v}]))};
      debutGeste();
      ev.preventDefault();
    });
    cible.addEventListener('pointermove', ev => {
      if (!pointeurs.has(ev.pointerId) || !base) return;
      pointeurs.set(ev.pointerId, {x: ev.clientX, y: ev.clientY});
      const k = pxParMm(), ids = [...pointeurs.keys()];
      if (ids.length >= 2 && base.pts.size >= 2) {
        const [a, b] = ids, a0 = base.pts.get(a), b0 = base.pts.get(b), a1 = pointeurs.get(a), b1 = pointeurs.get(b);
        if (a0 && b0) {
          const d0 = Math.hypot(a0.x - b0.x, a0.y - b0.y), d1 = Math.hypot(a1.x - b1.x, a1.y - b1.y);
          const bn = bornesE(); etat.e = borne(base.e * d0 / Math.max(10, d1), bn.min, bn.max);
          etat.centre = {dx: base.centre.dx + ((a1.x + b1.x) - (a0.x + b0.x)) / 2 / k.x, dy: base.centre.dy + ((a1.y + b1.y) - (a0.y + b0.y)) / 2 / k.y};
        }
      } else {
        const p0 = base.pts.get(ids[0]), p1 = pointeurs.get(ids[0]);
        if (p0) etat.centre = {dx: base.centre.dx + (p1.x - p0.x) / k.x, dy: base.centre.dy + (p1.y - p0.y) / k.y};
      }
      bornerCentre(); demanderGeste();
    });
    const lacher = ev => {
      if (!pointeurs.delete(ev.pointerId)) return;
      if (pointeurs.size) { base = {centre: {...etat.centre}, e: etat.e, pts: new Map([...pointeurs].map(([k, v]) => [k, {...v}]))}; return; }
      base = null; finGeste();
    };
    cible.addEventListener('pointerup', lacher);
    cible.addEventListener('pointercancel', lacher);
    let roueMinuteur = 0;
    cible.addEventListener('wheel', ev => {
      if (etape !== 2 || !etat.lieu || !sq) return;
      ev.preventDefault();
      if (!gesteActif) debutGeste();
      const bn = bornesE(); etat.e = borne(etat.e * Math.exp(borne(ev.deltaY, -120, 120) * .0025), bn.min, bn.max);
      bornerCentre(); demanderGeste(); majCadrage();
      clearTimeout(roueMinuteur); roueMinuteur = setTimeout(finGeste, 220);
    }, {passive: false});
    cible.addEventListener('keydown', ev => {
      if (etape !== 2 || !etat.lieu) return;
      const pas = ev.shiftKey ? 5 : 1, d = {ArrowLeft: [-pas, 0], ArrowRight: [pas, 0], ArrowUp: [0, -pas], ArrowDown: [0, pas]}[ev.key];
      if (d) { ev.preventDefault(); if (!gesteActif) debutGeste(); etat.centre = {dx: etat.centre.dx + d[0], dy: etat.centre.dy + d[1]}; bornerCentre(); demanderGeste(); clearTimeout(roueMinuteur); roueMinuteur = setTimeout(finGeste, 300); return; }
      if (['+', '=', '-', '_'].includes(ev.key)) { ev.preventDefault(); const f = ev.shiftKey ? 1.2 : 1.05; changerEchelle(etat.e * (ev.key === '+' || ev.key === '=' ? 1 / f : f), {graverAussi: false}); clearTimeout(roueMinuteur); roueMinuteur = setTimeout(finGeste, 300); }
    });

    /* ------------------------------------------------------------------
       Branchements
       ------------------------------------------------------------------ */
    root.addEventListener('click', ev => {
      const b = ev.target.closest('button,[data-carte-photo]');
      if (!b || !root.contains(b) && !(guide && guide.contains(b))) return;
      if (b.matches('[data-carte-suivant]')) aller(+b.dataset.carteSuivant + 1);
      else if (b.matches('[data-carte-aller]')) aller(+b.dataset.carteAller);
      else if (b.matches('[data-carte-cran]')) { const a = etat.eAuto || etat.e; changerEchelle({rue: a * .6, quartier: a, ville: a * 2}[b.dataset.carteCran]); }
      else if (b.matches('[data-carte-recentrer]')) { etat.centre = {dx: 0, dy: 0}; if (sq && geo) debutGeste(); finGeste(); }
      else if (b.matches('[data-carte-zoom-plus]')) changerEchelle(etat.e / 1.25);
      else if (b.matches('[data-carte-zoom-moins]')) changerEchelle(etat.e * 1.25);
      else if (b.matches('[data-carte-elargir]')) changerEchelle(+b.dataset.carteElargir);
      else if (b.matches('[data-carte-reessayer]')) { erreurCarte = ''; carte ? graver() : preparer(); }
      else if (b.matches('[data-carte-changer-lieu]')) { $('[data-carte-precision]').hidden = true; $('[data-carte-choisi]').hidden = true; if (guide && guide.open) guide.dataset.precision = 'false'; champRecherche()?.focus(); }
      else if (b.matches('[data-carte-ouvrir],[data-carte-flottant-ouvrir]')) {
        if (b.matches('[data-carte-flottant-ouvrir]') && root.dataset.pret === 'true') { ajouter(); return; }
        ouvrirGuide({focusRecherche: !etat.lieu});
      }
      else if (b.matches('[data-carte-ajouter],[data-carte-ajout-page]')) ajouter();
      else if (b.matches('[data-carte-copier]')) copierLien(b);
      else if (b.matches('[data-carte-guide-fermer]')) fermerGuide();
      else if (b.matches('[data-carte-guide-retour]')) { if (etape === 0) fermerGuide(); else aller(etape - 1); }
      else if (b.matches('[data-carte-guide-suivant]')) aller(etape + 1);
      else if (b.matches('[data-carte-guide-etape]')) aller(+b.dataset.carteGuideEtape);
      else if (b.matches('[data-carte-mot]')) debutMots(b.dataset.carteMot, true);
      else if (b.matches('[data-carte-mot-suivant]')) motSuivant();
      else if (b.matches('[data-carte-mots-fin]')) finMots(true);
      else if (b.matches('[data-carte-panier-reessayer]')) ajouter();
      else if (b.matches('[data-carte-panier-fermer]')) dialoguePanier.close();
      else if (b.matches('[data-carte-photo]')) voirPhoto(b);
      else if (b.matches('[data-carte-creation]')) voirCreation();
    });
    root.addEventListener('change', ev => {
      const x = ev.target;
      if (x.matches('[data-carte-forme]')) changerForme(x.value);
      else if (x.matches('[data-carte-trou]')) changerTrou(x.checked);
      else if (x.matches('[data-carte-style]')) regler('style', x.value);
      else if (x.matches('[data-carte-repere]')) regler('repere', x.value);
      else if (x.matches('[data-carte-eau-choix]')) regler('eau', x.value);
      else if (x.matches('[data-carte-date-format]')) { etat.date.fmt = DATES.includes(x.value) ? x.value : 'points'; majChamps(); apresTexte(); if (guide && guide.dataset.mot === 'date' && etat.date.fmt === 'libre') $('[data-carte-date-libre]').focus(); }
      else if (x.matches('[data-carte-coords-format]')) { etat.coords.fmt = COORDS.includes(x.value) ? x.value : 'dms'; majChamps(); apresTexte(); }
      else if (x.matches('[data-carte-cadeau]')) { etat.cadeau.on = x.checked; majEtapes(); sauver(); }
      else if (x.matches('[data-carte-echelle]')) changerEchelle(curseurVersE(+x.value));
    });
    root.addEventListener('input', ev => {
      const x = ev.target;
      if (x.matches('[data-carte-date]')) { etat.date.iso = /^\d{4}-\d{2}-\d{2}$/.test(x.value) ? x.value : ''; apresTexte(); }
      else if (x.matches('[data-carte-date-libre]')) { etat.date.libre = x.value.slice(0, 28); apresTexte(); }
      else if (x.matches('[data-carte-lieu-texte]')) { etat.lieuTexte = x.value.toLocaleUpperCase(en ? 'en' : 'fr').slice(0, 32); etat.lieuAuto = false; alerteLatin = ''; apresTexte(); }
      else if (x.matches('[data-carte-cadeau-msg]')) { etat.cadeau.msg = x.value.slice(0, 200); sauver(); }
      else if (x.matches('[data-carte-echelle]')) changerEchelle(curseurVersE(+x.value), {graverAussi: false});
      if (guide && guide.dataset.mot) requestAnimationFrame(camera);
    });
    root.addEventListener('keydown', ev => {
      if (ev.key === 'Enter' && guide && guide.dataset.mot && ev.target.matches('input')) { ev.preventDefault(); motSuivant(); }
    });
    if (guide) {
      guide.addEventListener('cancel', ev => { ev.preventDefault(); if (guide.dataset.mot) finMots(true); else fermerGuide(); });
      guide.addEventListener('close', () => { if (root.dataset.mobileGuide === 'true') fermerGuide(); });
    }
    if (dialoguePanier) dialoguePanier.addEventListener('cancel', ev => { if ($('[data-carte-panier-actions]').hidden) ev.preventDefault(); });
    async function copierLien(b) {
      const url = lienCarte(), texte0 = b.textContent;
      let ok = false;
      try { await navigator.clipboard.writeText(url); ok = true; } catch { /* repli ci-dessous */ }
      if (!ok) { try { const ta = document.createElement('textarea'); ta.value = url; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0'; document.body.append(ta); ta.select(); ok = document.execCommand('copy'); ta.remove(); } catch { ok = false; } }
      b.textContent = ok ? t('Lien copié ✓', 'Link copied ✓') : url;
      setTimeout(() => { b.textContent = texte0; }, 2600);
    }
    function voirPhoto(b) {
      const im = $('.mt-carte-media'); if (!im) return;
      im.removeAttribute('srcset'); im.src = b.dataset.src; im.alt = b.dataset.alt || '';
      root.dataset.vue = 'photo';
      for (const v of $$('.mt-carte-vignette')) v.setAttribute('aria-pressed', String(v === b));
    }
    function voirCreation() {
      root.dataset.vue = 'carte';
      for (const v of $$('.mt-carte-vignette')) v.setAttribute('aria-pressed', String(v.matches('[data-carte-creation]')));
    }
    let redimMinuteur = 0, derniereTaille = '';
    function redimensionner() {
      clearTimeout(redimMinuteur);
      redimMinuteur = setTimeout(() => {
        const {w, h} = taillePiece(), k = w + 'x' + h;
        if (k === derniereTaille && root.dataset.peint === 'true') { camera(); return; }
        derniereTaille = k; peindre().then(() => { if (etape === 2 && sq) demanderGeste(); });
      }, 90);
    }
    if (typeof ResizeObserver !== 'undefined') new ResizeObserver(redimensionner).observe(scene);
    window.addEventListener('resize', () => { guideViewport(); redimensionner(); majFlottant(); });
    if (window.visualViewport) { visualViewport.addEventListener('resize', guideViewport); visualViewport.addEventListener('scroll', guideViewport); }
    if (typeof IntersectionObserver !== 'undefined' && $('[data-carte-ouvrir]')) new IntersectionObserver(() => majFlottant(), {threshold: [0, 1]}).observe($('[data-carte-ouvrir]'));
    window.addEventListener('scroll', () => requestAnimationFrame(majFlottant), {passive: true});
    mobile.addEventListener?.('change', () => { if (!mobile.matches && guide && guide.open) fermerGuide(); majFlottant(); redimensionner(); });

    // Ordinateur : l'aperçu collant se cale SOUS l'en-tête collant du site (COPIE DE mt-photo-product.liquid).
    (function () {
      const mq = matchMedia('(min-width:881px)'); let f = 0, dernier = -1;
      function calc() {
        f = 0; let b = 0;
        if (mq.matches) document.querySelectorAll('.shopify-section-group-header-group,sticky-header,header').forEach(e => { const s = getComputedStyle(e); if ((s.position !== 'sticky' && s.position !== 'fixed') || s.visibility === 'hidden' || s.display === 'none') return; const x = e.getBoundingClientRect(); if (x.top <= 1 && x.bottom > b && x.height < 300 && x.width > innerWidth / 2) b = x.bottom; });
        b = Math.max(0, Math.round(b)); if (b !== dernier) { dernier = b; root.style.setProperty('--mtx-ph-top', b + 'px'); }
      }
      const planifier = () => { if (!f) f = requestAnimationFrame(calc); };
      addEventListener('scroll', planifier, {passive: true}); addEventListener('resize', planifier); calc();
    })();

    /* ------------------------------------------------------------------
       Démarrage
       ------------------------------------------------------------------ */
    if (!R() || !L() || !peintre) {
      dire(t('La création de carte est momentanément indisponible. Réessayez dans quelques minutes.', 'Map creation is temporarily unavailable. Please try again in a few minutes.'), true);
      root.removeAttribute('aria-busy');
      return;
    }
    R().config({assets: {rendu: config.assets.rendu, worker: config.assets.worker, clipper: config.assets.clipper}, donnees: config.donnees, locale: config.locale});
    root.dataset.lieu = 'false'; root.dataset.vue = 'carte'; root.dataset.peint = 'false';
    window.CartePeintre.polices().catch(() => {}).then(() => {
      // Reprise : lien #carte= (prioritaire, « Modifier » depuis le panier compris), sinon brouillon de cet appareil.
      const hash = new URLSearchParams(location.hash.slice(1));
      const lien = hash.get('carte');
      let repris = null, etapeReprise = 5;
      if (lien) repris = decoder(lien);
      if (lien && !repris) dire(t('Ce lien de carte est incomplet : recommencez à partir de votre lieu.', 'This map link is incomplete: start again from your place.'), true);
      if (!repris) { const b = lireBrouillon(); if (b) { repris = b.etat; etapeReprise = b.etape; } }
      if (repris) {
        etat = repris; root.dataset.shape = etat.forme; root.dataset.lieu = 'true'; root.dataset.cree = 'true';
        for (let i = 0; i < etapeReprise; i++) faites.add(i);
        etape = etapeReprise;
        if (!etat.lieuTexte && etat.lieuAuto) etiqueter(etat.lieu);
        preparer();
      }
      if (hash.get('k') || lien) chargerPont().catch(() => {});
      monterLieu();
      majTout(); peindre();
      root.removeAttribute('aria-busy');
    });
  }
  function demarrer() { document.querySelectorAll('[data-carte-product]').forEach(mount); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', demarrer); else demarrer();
  document.addEventListener('shopify:section:load', ev => ev.target.querySelectorAll('[data-carte-product]').forEach(mount));
})();
